import {Button, SmallLoader} from '@kit';

import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {useWizardSpellsQuery} from '../../../generated/types';
import {getApolloError, humanNumber} from '../../../components/utils';
import {Link, buildUrl} from '../../../lib/routes';
import {Seo, buildKeywords, pageJsonLd, sectionCrumbs} from '../../../lib/seo';
import {SECTIONS} from '../../../lib/seo/sections';
import {ToolCrumbs, visuallyHidden} from '../tool-crumbs';
import {describeAppearance} from './appearance';
import {COPY_PRICE, MAX_LEVEL, Spellbook, generateSpellbook} from './generate-spellbook';

import css from './spellbook.module.css';

const LEVELS = Array.from({length: MAX_LEVEL}, (_, index) => index + 1);
const gold = (value: number) => `${humanNumber(value)} ЗМ`;

const SpellbookSeo: React.FC = () => {
	const {title, description, heading} = SECTIONS.spellbook;
	const path = buildUrl('toolSpellbook');

	return (
		<Seo
			title={title}
			description={description}
			canonicalPath={path}
			keywords={buildKeywords('spellbook')}
			jsonLd={pageJsonLd({path, title, description, crumbs: sectionCrumbs('tools', {name: heading, path})})}
		/>
	);
};

const Result: React.FC<{book: Spellbook}> = ({book}) => (
	<section className={css.result} aria-live="polite">
		<h2 className={css.resultTitle}>
			Книга заклинаний: до {book.maxLevel}-го круга, {book.circles.reduce((sum, circle) => sum + circle.spells.length, 0)}{' '}
			заклинаний
		</h2>
		<p className={css.appearance}>
			<b>Внешний вид.</b> {describeAppearance(book.appearance)}
		</p>
		{book.circles.map((circle) => (
			<div key={circle.level} className={css.circle}>
				<h3 className={css.circleTitle}>
					{circle.level}-й круг <span className={css.price}>по {gold(COPY_PRICE[circle.level])}</span>
				</h3>
				<ul className={css.spells}>
					{circle.spells.map((spell) => (
						<li key={spell.id}>
							<Link route="spell" params={{id: spell.id}}>
								{spell.title}
							</Link>
						</li>
					))}
				</ul>
			</div>
		))}

		<dl className={css.totals}>
			<div>
				<dt>
					<Link route="item" params={{id: 'spellbook'}}>
						Книга заклинаний
					</Link>
				</dt>
				<dd>{gold(book.blankPrice)}</dd>
			</div>
			<div>
				<dt>Копирование заклинаний</dt>
				<dd>{gold(book.copyTotal)}</dd>
			</div>
			<div className={css.total}>
				<dt>Итого</dt>
				<dd>{gold(book.total)}</dd>
			</div>
		</dl>
	</section>
);

export const SpellbookGenerator: React.FC = () => {
	const {data, loading, error} = useWizardSpellsQuery({ssr: false});
	const [maxLevel, setMaxLevel] = useState(3);
	const [seed, setSeed] = useState(0);
	const [ready, setReady] = useState(false);

	useEffect(() => setReady(true), []);

	const book = useMemo(
		() => (ready && data ? generateSpellbook(data.spells, maxLevel) : undefined),
		[ready, data, maxLevel, seed],
	);
	const regenerate = useCallback(() => setSeed((value) => value + 1), []);

	return (
		<div className={css.page}>
			<SpellbookSeo />
			<ToolCrumbs trail={[{title: SECTIONS.spellbook.heading}]} />
			<h1 className={visuallyHidden}>{SECTIONS.spellbook.heading}</h1>
			<p className={css.lead}>
				Случайная книга заклинаний волшебника. Выберите максимальный круг: заклинаний будет примерно столько же, сколько у
				волшебника, которому этот круг только стал доступен, плюс-минус 20%.
			</p>

			<div className={css.controls}>
				<div className={css.levels} role="group" aria-label="Максимальный круг заклинаний">
					{LEVELS.map((level) => (
						<Button
							key={level}
							size="small"
							shape="circular"
							appearance="outline"
							checked={level === maxLevel}
							onClick={() => setMaxLevel(level)}
							aria-label={`${level}-й круг`}
						>
							{level}
						</Button>
					))}
				</div>
				<Button appearance="primary" onClick={regenerate}>
					Сгенерировать заново
				</Button>
			</div>

			{error ? (
				<div className={css.error}>{getApolloError(error).message}</div>
			) : loading || !book ? (
				<SmallLoader />
			) : (
				<Result book={book} />
			)}

		</div>
	);
};
